import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MemberSimpleDTO } from '../../../services/apiClient';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { useDedicatedStatusApi } from '../../../hooks/useDedicatedStatusApi';
import SimpleAlert from '../../../components/common/SimpleAlert';
import { ComponentConstants } from '../../../constants/ComponentConstants';

const TYPE_MAPPER: Record<string, string> = {
  endorsement: 'Endorsement Certificate',
  dedicatedStatus: 'Dedicated Status Certificate',
  activity: 'Activity Report',
  // Add more mappings here as needed
};

const VerifyCertificate: React.FC = () => {
  const { memberid, type, typeId } = useParams<{
    memberid: string;
    type?: string;
    typeId?: string;
  }>();
  const navigate = useNavigate();
  const backgroundImage = '/images/NS_Pattern%201_Navy_2025.webp';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberData, setMemberData] = useState<MemberSimpleDTO | null>(null);
  const [certificateType, setCertificateType] = useState<string | null>(null);
  const [certificateCode, setCertificateCode] = useState<string | null>(null);
  const [isForfeited, setIsForfeited] = useState<boolean>(false);
  const { getSimplifiedById } = useMemberApi();
  const { checkIsForfeited } = useDedicatedStatusApi();

  useEffect(() => {
    const verifyMember = async () => {
      try {
        setLoading(true);
        setError(null);

        // Basic validation
        if (!memberid) {
          setError('Member ID is required');
          return;
        }

        const response = await getSimplifiedById(memberid);
        if (response && response.data) {
          setMemberData(response.data);
        }

        // Check forfeiture status for dedicated status certificates
        const isDedicatedStatus =
          type === 'dedicatedStatus' ||
          type?.toLowerCase() === ComponentConstants.DEDICATED_COURSES.toLowerCase();
        if (isDedicatedStatus && typeId) {
          const forfeited = await checkIsForfeited(typeId);
          setIsForfeited(forfeited);
        }
      } catch (err) {
        setError('An error occurred while verifying the endorsement');
        console.error('Verification error:', err);
      } finally {
        setLoading(false);
      }
    };

    verifyMember();
  }, [memberid]);

  useEffect(() => {
    if (type) {
      const mappedType = TYPE_MAPPER[type.toLowerCase()] || type;
      setCertificateType(mappedType);
    } else {
      setCertificateType(null);
    }
  }, [type]);

  useEffect(() => {
    if (typeId) {
      setCertificateCode(typeId);
    } else {
      setCertificateCode(null);
    }
  }, [typeId]);

  const isActiveMember = (status?: string): boolean => {
    return status?.toLowerCase() === 'active';
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
      <div
        className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />

      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl text-center z-10">
        <img
          src="/images/logo-linnear-navy.png"
          alt="Natshoot Logo"
          className="mx-auto w-48 mb-8"
        />

        <div className="space-y-6">
          {loading ? (
            <div className="text-sm text-gray-600">Loading...</div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : memberData ? (
            <div className="space-y-6">
              {isForfeited ? (
                <SimpleAlert
                  variant="error"
                  message={`This certificate is no longer valid. ${memberData.title} ${memberData.memberFirstName} ${memberData.memberLastName}'s dedicated status has been forfeited. The information below is provided for audit purposes only.`}
                />
              ) : isActiveMember(memberData.status) ? (
                <SimpleAlert
                  variant="success"
                  message={`This certificate is authenticated and valid. ${memberData.title} ${memberData.memberFirstName} ${memberData.memberLastName} is an active member of Natshoot.`}
                />
              ) : null}

              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                {/* Header Section */}
                <div className="border-b pb-4 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Name</span>
                    <span className="text-gray-900 font-medium">
                      {memberData.title} {memberData.memberFirstName} {memberData.memberLastName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">ID Number</span>
                    <span className="text-gray-900">{memberData.maskedIdNumber}</span>
                  </div>
                </div>

                {/* Certificate Details */}
                {(certificateType || certificateCode) && (
                  <div className="border-b pb-4">
                    {certificateType && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Certificate Type</span>
                        <span className="text-gray-900">{certificateType}</span>
                      </div>
                    )}
                    {certificateCode && (
                      <div className="flex justify-between items-center text-sm mt-2">
                        <span className="text-gray-500">Certificate Code</span>
                        <span className="text-gray-900">{certificateCode}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Member Details Grid */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Membership Type</span>
                    <span className="text-gray-900">{memberData.membershipType}</span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">FCA Status</span>
                    <span className="text-gray-900">{memberData.fcaStatus}</span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Member Status</span>
                    <span className="text-gray-900">{memberData.status}</span>
                  </div>

                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Paid Up To</span>
                    <span className="text-gray-900">
                      {memberData.membershipType?.toLowerCase().includes('life')
                        ? 'Life Member'
                        : memberData.paidUpTo
                          ? new Date(memberData.paidUpTo).toLocaleDateString()
                          : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-600">No member data found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
